import React, { useState } from 'react';
import { X, Save, Palette } from 'lucide-react';

const EditAllCardsModal = ({ event, onClose, onSave }) => {
    const [cardStyle, setCardStyle] = useState({
        eventNameColor: '#ffffff',
        eventNameSize: '18px',
        nameColor: '#ffffff',
        nameSize: '24px',
        regIdColor: '#ffffff',
        regIdSize: '12px',
    });

    const handleSave = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}/participants/update-all-styles`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cardStyle })
                }
            );
            const data = await response.json();
            if (data.success) {
                onSave();
                onClose();
            }
        } catch (error) {
            console.error('Error updating card styles:', error);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Palette className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Edit All ID Cards</h2>
                            <p className="text-sm text-gray-600 mt-1">Apply styles to all participant cards</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Event Name Styling */}
                    <div className="space-y-3">
                        <h3 className="font-semibold text-gray-900">Event Name</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Color
                                </label>
                                <input
                                    type="color"
                                    value={cardStyle.eventNameColor}
                                    onChange={(e) => setCardStyle({ ...cardStyle, eventNameColor: e.target.value })}
                                    className="w-full h-10 rounded border border-gray-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Font Size
                                </label>
                                <select
                                    value={cardStyle.eventNameSize}
                                    onChange={(e) => setCardStyle({ ...cardStyle, eventNameSize: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="14px">Small (14px)</option>
                                    <option value="16px">Medium (16px)</option>
                                    <option value="18px">Large (18px)</option>
                                    <option value="20px">X-Large (20px)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Name Styling */}
                    <div className="space-y-3">
                        <h3 className="font-semibold text-gray-900">Participant Name</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Color
                                </label>
                                <input
                                    type="color"
                                    value={cardStyle.nameColor}
                                    onChange={(e) => setCardStyle({ ...cardStyle, nameColor: e.target.value })}
                                    className="w-full h-10 rounded border border-gray-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Font Size
                                </label>
                                <select
                                    value={cardStyle.nameSize}
                                    onChange={(e) => setCardStyle({ ...cardStyle, nameSize: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="18px">Small (18px)</option>
                                    <option value="20px">Medium (20px)</option>
                                    <option value="24px">Large (24px)</option>
                                    <option value="28px">X-Large (28px)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Dynamic Fields Styling */}
                    {event.participantFields?.filter(f => !f.isDefault).map(field => (
                        <div key={field.id} className="space-y-3">
                            <h3 className="font-semibold text-gray-900 capitalize">{field.fieldName}</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color
                                    </label>
                                    <input
                                        type="color"
                                        value={cardStyle[`${field.fieldName}Color`] || '#ffffff'}
                                        onChange={(e) => setCardStyle({ ...cardStyle, [`${field.fieldName}Color`]: e.target.value })}
                                        className="w-full h-10 rounded border border-gray-300"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Font Size
                                    </label>
                                    <select
                                        value={cardStyle[`${field.fieldName}Size`] || '14px'}
                                        onChange={(e) => setCardStyle({ ...cardStyle, [`${field.fieldName}Size`]: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="12px">X-Small (12px)</option>
                                        <option value="14px">Small (14px)</option>
                                        <option value="16px">Medium (16px)</option>
                                        <option value="18px">Large (18px)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Registration ID Styling */}
                    {event.generateRegistrationId && (
                        <div className="space-y-3">
                            <h3 className="font-semibold text-gray-900">Registration ID</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color
                                    </label>
                                    <input
                                        type="color"
                                        value={cardStyle.regIdColor}
                                        onChange={(e) => setCardStyle({ ...cardStyle, regIdColor: e.target.value })}
                                        className="w-full h-10 rounded border border-gray-300"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Font Size
                                    </label>
                                    <select
                                        value={cardStyle.regIdSize}
                                        onChange={(e) => setCardStyle({ ...cardStyle, regIdSize: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="10px">X-Small (10px)</option>
                                        <option value="12px">Small (12px)</option>
                                        <option value="14px">Medium (14px)</option>
                                        <option value="16px">Large (16px)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditAllCardsModal;